import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import { dispatch } from "../../state/store";
import {
  setDevicesRefresh,
  setNotificationCampaignsRefresh,
  setNotificationCenterActiveTab,
  setNotificationsRefresh,
  setUsersRefresh
} from "../../state/ducks";

import {
  alignItems,
  campaignStatus,
  notificationSections,
  routePaths,
  strCode
} from "../../consts";
import { t } from "../../services";
import { withSection } from "../../hoc";
import { notificationUtils } from "../../utils";
import {
  useFetchNotificationCampaignDetails,
  useCreateNotificationCampaign,
  useDeactivateNotificationCampaign,
  useUpdateNotificationCampaign
} from "../../apiHooks";
import { useFetchData } from "./hooks/useFetchData";

import {
  NewNotificationCampaignModal,
  NotificationCampaignTable,
  NotificationsTable
} from "../../containers";
import {
  FlexContainers,
  Heading5,
  PageContainer,
  PageSelector,
  RefreshButton,
  SearchInput,
  SmallButton,
  WrapperContainer
} from "../../components";
import NotificationCampaignReportModal from "../../containers/Campaigns/NotificationCampaign/NotificationCampaignReportModal/NotificationCampaignReportModal";
import { PageSelectorWrapper } from "./styled";

const notificationPages = [
  { id: notificationSections.CAMPAIGNS, title: strCode.CAMPAIGNS_TITLE },
  { id: notificationSections.MESSAGES, title: strCode.MESSAGES },
  { id: notificationSections.SYSTEM_MESSAGES, title: strCode.SYSTEM_MESSAGES }
];

const NotificationManagementPage = () => {
  const history = useHistory();
  const [isReportModalActive, setIsReportModalActive] = useState(false);

  const [newNotificationCampaignModalActive, setNewNotificationCampaignModalActive] =
    useState(false);

  const notificationCampaignToEdit = useSelector(
    (store) => store.notificationCampaigns?.notificationCampaignToEdit
  );
  const notificationCenterActiveTab = useSelector(
    (store) => store.global?.notificationCenterActiveTab
  );

  const [searchValue, setSearchValue] = useState("");
  const [activePage, setActivePage] = useState(
    notificationCenterActiveTab || notificationSections.CAMPAIGNS
  );

  const selectedNotificationCampaignName = useMemo(() => {
    return notificationCampaignToEdit?.campaignName;
  }, [notificationCampaignToEdit]);

  const {
    notifications,
    systemNotifications,
    notificationCampaigns,
    notificationsLoading,
    notificationCampaignsLoading,
    devicesLoading,
    usersLoading
  } = useFetchData();

  const {
    fetchCallback: fetchNotificationCampaignDetails,
    data: notificationCampaignDetails,
    isLoading: isNotificationCampaignDetailsLoading,
    refresh: notificationCampaignDetailsRefresh
  } = useFetchNotificationCampaignDetails();

  const {
    mutationCallback: createNotificationCampaign,
    loading: createNotificationCampaignLoading,
    error: createNotificationCampaignError
  } = useCreateNotificationCampaign();

  const {
    mutationCallback: deactivateNotificationCampaign,
    loading: deactivateNotificationCampaignLoading,
    error: deactivateNotificationCampaignError
  } = useDeactivateNotificationCampaign();

  const {
    mutationCallback: updateNotificationCampaign,
    loading: updateNotificationCampaignLoading,
    error: updateNotificationCampaignError
  } = useUpdateNotificationCampaign();

  const availableSystemNotificationOptions = useMemo(() => {
    return notificationUtils.getAvailableSystemNotificationOptions();
  }, [systemNotifications?.length]);

  const handleFilterChange = (value) => {
    setSearchValue(value);
  };

  const onCreateNotification = () => {
    if (activePage === notificationSections.CAMPAIGNS) {
      setNewNotificationCampaignModalActive(true);
    } else {
      const isSystemNotification = activePage === notificationSections.SYSTEM_MESSAGES;
      let createNotificationRoute = routePaths.NOTIFICATIONS_CREATE;

      if (isSystemNotification) {
        createNotificationRoute = `${createNotificationRoute}/true`;
      }

      history.push(createNotificationRoute);
    }
  };

  useEffect(() => {
    if (notificationCampaignToEdit && !newNotificationCampaignModalActive && !isReportModalActive) {
      const isScheduled = notificationCampaignToEdit?.status === campaignStatus.scheduled;

      if (isScheduled) {
        setNewNotificationCampaignModalActive(true);
      } else {
        setIsReportModalActive(true);
      }
    }
  }, [notificationCampaignToEdit]);

  useEffect(() => {
    if (selectedNotificationCampaignName && isReportModalActive) {
      fetchNotificationCampaignDetails(
        selectedNotificationCampaignName,
        notificationCampaignDetailsRefresh
      );
    }
  }, [notificationCampaignDetailsRefresh, isReportModalActive]);

  const onRefreshAll = () => {
    dispatch(setNotificationsRefresh(true));
    dispatch(setNotificationCampaignsRefresh(true));
    dispatch(setDevicesRefresh(true));
    dispatch(setUsersRefresh(true));
  };

  const onSetActivePage = (page) => {
    setActivePage(page);
    dispatch(setNotificationCenterActiveTab(page));
  };

  return (
    <PageContainer alignItems={alignItems.start}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        mb={16}
        alignItems="start"
        width="100%">
        <WrapperContainer Container={FlexContainers.FlexRowStart} width="100%" gap={16}>
          <Heading5>{t(strCode.NOTIFICATION_CENTER_TITLE)}</Heading5>

          <RefreshButton
            isLoading={
              notificationsLoading || notificationCampaignsLoading || devicesLoading || usersLoading
            }
            handleRefresh={onRefreshAll}
          />
        </WrapperContainer>

        <WrapperContainer
          Container={FlexContainers.FlexColumnStart}
          alignItems={alignItems.start}
          gap={16}
          width="100%">
          <PageSelectorWrapper>
            <PageSelector
              pages={notificationPages}
              activePage={activePage}
              setActivePage={onSetActivePage}
            />
          </PageSelectorWrapper>

          <WrapperContainer Container={FlexContainers.FlexRowBetween} gap={8} width="100%">
            <WrapperContainer width="100%" maxWidth={329}>
              <SearchInput
                id="notifications-search"
                value={searchValue}
                setValue={handleFilterChange}
                placeholder={t(strCode.SEARCH_PLACEHOLDER)}
              />
            </WrapperContainer>

            <SmallButton
              onClick={() => onCreateNotification()}
              disabled={
                (activePage === notificationSections.CAMPAIGNS &&
                  (devicesLoading || usersLoading)) ||
                (activePage === notificationSections.SYSTEM_MESSAGES &&
                  availableSystemNotificationOptions.length === 0)
              }
              tooltipLabel={t(strCode.NEW_NOTIFICATION_TOOLTIP_LABEL)}
              xShift={-64}>
              {t(strCode.NEW_BTN)}
            </SmallButton>
          </WrapperContainer>
        </WrapperContainer>
      </WrapperContainer>

      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        width="100%">
        {activePage === notificationSections.CAMPAIGNS && (
          <NotificationCampaignTable
            notificationCampaignData={notificationCampaigns}
            filterValue={searchValue}
          />
        )}

        {activePage === notificationSections.MESSAGES && (
          <NotificationsTable
            notificationsData={notifications ? notifications : []}
            filterValue={searchValue}
          />
        )}

        {activePage === notificationSections.SYSTEM_MESSAGES && (
          <NotificationsTable
            notificationsData={systemNotifications ? systemNotifications : []}
            filterValue={searchValue}
          />
        )}
      </WrapperContainer>

      <NewNotificationCampaignModal
        notificationCampaignData={notificationCampaignToEdit}
        active={newNotificationCampaignModalActive}
        setActive={setNewNotificationCampaignModalActive}
        loading={
          createNotificationCampaignLoading ||
          deactivateNotificationCampaignLoading ||
          updateNotificationCampaignLoading
        }
        error={
          createNotificationCampaignError ||
          deactivateNotificationCampaignError ||
          updateNotificationCampaignError
        }
        onSave={createNotificationCampaign}
        onUpdate={updateNotificationCampaign}
        onDeactivate={deactivateNotificationCampaign}
      />

      <NotificationCampaignReportModal
        active={isReportModalActive}
        notificationCampaignDetails={notificationCampaignDetails}
        notificationCampaignData={notificationCampaignToEdit}
        notificationCampaignDetailsLoading={isNotificationCampaignDetailsLoading}
        loading={
          isNotificationCampaignDetailsLoading ||
          notificationCampaignsLoading ||
          deactivateNotificationCampaignLoading ||
          updateNotificationCampaignLoading
        }
        error={updateNotificationCampaignError}
        setActive={setIsReportModalActive}
        onSave={updateNotificationCampaign}
        onDeactivate={deactivateNotificationCampaign}
      />
    </PageContainer>
  );
};

export default withSection(NotificationManagementPage);
